import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useMe } from "@/shared/api/auth";
import { useUsageStatus } from "@/shared/api/usage";
import { setTrackingActive, track } from "@/shared/lib/track";

/** A melléklet oldalai a használati statisztikában (rendszerterv 9.1) — a kulcsokat a
 *  szerver `app/book/analytics.py` `PAGES` szótára fordítja. Új oldal = egy sor itt és ott. */
const PAGES: Array<[RegExp, string]> = [
  [/^\/app\/book\/?$/, "home"],
  [/^\/app\/book\/fejezetek\/\d+/, "chapter"],
  [/^\/app\/book\/gyakorlatok\/?$/, "exercises"],
  [/^\/app\/book\/gyakorlatok\/\d+/, "exercise"],
  [/^\/app\/book\/ajanlo/, "readings"],
  [/^\/app\/book\/tapasztalatok/, "experiences"],
  [/^\/app\/book\/fogalomtar/, "glossary"],
  [/^\/app\/book\/hibajegyzek/, "errata"],
  [/^\/app\/book\/kereses/, "search"],
  [/^\/app\/book\/profil/, "profile"],
];

/** A `BackState` felirata alapján: honnan nyitották meg a gyakorlatot. */
const FROM_LABEL: Record<string, string> = {
  Kezdőlap: "home",
  Gyakorlattár: "exercises",
  Keresés: "search",
  Tapasztalatok: "experiences",
};

export function exerciseSource(state: unknown): string {
  const s = state as { back?: unknown; from?: unknown } | null;
  if (typeof s?.from === "string") return s.from;
  if (typeof s?.back !== "string") return "direct";
  if (/fejezet$/.test(s.back)) return "chapter";
  return FROM_LABEL[s.back] ?? "other";
}

export function pageKey(pathname: string): string | null {
  return PAGES.find(([re]) => re.test(pathname))?.[1] ?? null;
}

/** A melléklet keretében egyszer: lekéri, rögzít-e a szerver, és minden navigációnál
 *  naplózza az oldalt (+ a fejezet / gyakorlat megnyitását). */
export function useBookTracking(): void {
  const { data: me } = useMe();
  const { data: status } = useUsageStatus(Boolean(me));
  const location = useLocation();
  const last = useRef<string | null>(null);

  useEffect(() => {
    if (status) setTrackingActive(status.active);
  }, [status]);

  useEffect(() => {
    // navigációnként egyszer (a StrictMode kettős effektusa és a puszta újrarenderelés nem számít)
    if (last.current === location.key) return;
    last.current = location.key;
    const key = pageKey(location.pathname);
    if (!key) return;
    track("book.page.view", key);
    const id = location.pathname.match(/\/(\d+)\/?$/)?.[1];
    if (key === "chapter" && id) track("book.chapter.open", id);
    if (key === "exercise" && id) track("book.exercise.open", id, { from: exerciseSource(location.state) });
  }, [location]);
}
